/**
 * 博客顶部阅读导航：控制浮动显示和正文阅读进度。
 */
if (!customElements.get('article-navigation')) {
  class ArticleNavigation extends HTMLElement {
    constructor() {
      super();
      this._rafId = null;
      this._initRafId = null;
      this._resizeObserver = null;
      this._isSticky = false;
      this._onScroll = this.requestUpdate.bind(this);
      this._onResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this._initRafId = requestAnimationFrame(() => {
        this._initRafId = null;
        this.progressBar = this.querySelector('#progress-bar');
        this.articleElement = document.getElementById('main-article');
        this.titleElement = document.getElementById('post-title-wrapper');

        if (!this.progressBar || !this.articleElement || !this.titleElement) return;

        this.progressBar.setAttribute('role', 'progressbar');
        this.progressBar.setAttribute('aria-label', 'Article reading progress');
        this.progressBar.setAttribute('aria-valuemin', '0');
        this.progressBar.setAttribute('aria-valuemax', '100');

        this.cacheOffsets();
        window.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onResize);

        // 图片或动态模块改变正文高度时，重新计算进度范围。
        if ('ResizeObserver' in window) {
          this._resizeObserver = new ResizeObserver(this._onResize);
          this._resizeObserver.observe(this.articleElement);
        }

        this.update();
      });
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      this._resizeObserver?.disconnect();

      if (this._rafId) cancelAnimationFrame(this._rafId);
      if (this._initRafId) cancelAnimationFrame(this._initRafId);
      this._rafId = null;
      this._initRafId = null;
    }

    requestUpdate() {
      if (this._rafId) return;

      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        this.update();
      });
    }

    handleResize() {
      this.cacheOffsets();
      this.requestUpdate();
    }

    cacheOffsets() {
      const scrollY = window.scrollY;
      const titleRect = this.titleElement.getBoundingClientRect();
      const articleRect = this.articleElement.getBoundingClientRect();

      this._titleEnd = titleRect.bottom + scrollY;
      this._articleEnd = articleRect.bottom + scrollY;
      this._progressStart = this._titleEnd;
      this._progressEnd = Math.max(
        this._articleEnd - window.innerHeight,
        this._progressStart + 1
      );
    }

    update() {
      const scrollY = window.scrollY;
      const range = this._progressEnd - this._progressStart;
      const progress = Math.min(Math.max((scrollY - this._progressStart) / range, 0), 1);

      // scaleX 只触发合成层更新，比持续修改宽度更流畅。
      this.progressBar.style.transform = `scaleX(${progress})`;
      this.progressBar.setAttribute('aria-valuenow', Math.round(progress * 100));

      const shouldStick = scrollY > this._titleEnd && scrollY <= this._articleEnd;
      if (shouldStick !== this._isSticky) {
        this._isSticky = shouldStick;
        this.classList.toggle('navigation--sticky', shouldStick);
      }
    }
  }

  customElements.define('article-navigation', ArticleNavigation);
}


/**
 * 文章右侧提及产品栏：在评论区底部停止吸顶，避免越过评论内容继续下滑。
 */
if (!customElements.get('article-products-sticky')) {
  class ArticleProductsSticky extends HTMLElement {
    constructor() {
      super();
      this._rafId = null;
      this._resizeObserver = null;
      this._onScroll = this.requestUpdate.bind(this);
      this._onResize = this.requestUpdate.bind(this);
    }

    connectedCallback() {
      requestAnimationFrame(() => {
        this.article = this.closest('.blog-post-detail');
        this.products = this.article?.querySelector('.article-products');
        this.comments = this.article?.querySelector('.comments-wrapper');
        this.desktopQuery = window.matchMedia('(min-width: 1024px)');

        if (!this.products || !this.comments) return;

        this._controller = new AbortController();
        const { signal } = this._controller;
        window.addEventListener('scroll', this._onScroll, { passive: true, signal });
        window.addEventListener('resize', this._onResize, { signal });
        this.desktopQuery.addEventListener('change', this._onResize, { signal });

        if ('ResizeObserver' in window) {
          this._resizeObserver = new ResizeObserver(this._onResize);
          this._resizeObserver.observe(this.products);
          this._resizeObserver.observe(this.comments);
        }

        this.update();
      });
    }

    disconnectedCallback() {
      this._controller?.abort();
      this._resizeObserver?.disconnect();
      if (this._rafId) cancelAnimationFrame(this._rafId);
      this.products?.style.removeProperty('--article-products-sticky-offset');
    }

    requestUpdate() {
      if (this._rafId) return;
      this._rafId = requestAnimationFrame(() => {
        this._rafId = null;
        this.update();
      });
    }

    update() {
      if (!this.desktopQuery.matches) {
        this.products.style.removeProperty('--article-products-sticky-offset');
        return;
      }

      const stickyTop = parseFloat(getComputedStyle(this.products).top) || 0;
      const commentsBottom = this.comments.getBoundingClientRect().bottom + window.scrollY;
      const stickyBottom = window.scrollY + stickyTop + this.products.offsetHeight;
      const offset = Math.min(0, commentsBottom - stickyBottom);

      this.products.style.setProperty('--article-products-sticky-offset', offset + 'px');
    }
  }

  customElements.define('article-products-sticky', ArticleProductsSticky);
}


/**
 * 博客正文插图灯箱：仅处理正文图片，支持点击、键盘打开及遮罩关闭。
 */
if (!customElements.get('article-image-lightbox')) {
  class ArticleImageLightbox extends HTMLElement {
    connectedCallback() {
      this.article = this.closest('.blog-post-detail');
      this.content = this.article?.querySelector('.post-content');
      if (!this.content) {
        this.remove();
        return;
      }

      this._controller = new AbortController();
      const { signal } = this._controller;
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'article-image-lightbox__dialog';
      this.dialog.innerHTML = `
        <button class="article-image-lightbox__close" type="button" aria-label="关闭放大图片">×</button>
        <img class="article-image-lightbox__image" alt="">
      `;
      document.body.append(this.dialog);
      this.lightboxImage = this.dialog.querySelector('.article-image-lightbox__image');

      this.images = Array.from(this.content.querySelectorAll('img[src]'));
      this.images.forEach((image) => {
        image.classList.add('article-image-lightbox__trigger');
        image.dataset.articleImageLightbox = '';
        image.tabIndex = 0;
        image.setAttribute('role', 'button');
        image.setAttribute('aria-label', image.alt ? `放大查看：${image.alt}` : '放大查看图片');
      });

      this.content.addEventListener('click', this.onContentClick.bind(this), { signal });
      this.content.addEventListener('keydown', this.onContentKeydown.bind(this), { signal });
      this.dialog.addEventListener('click', this.onDialogClick.bind(this), { signal });
      this.dialog.addEventListener('cancel', this.onDialogCancel.bind(this), { signal });
    }

    disconnectedCallback() {
      this._controller?.abort();
      this.dialog?.remove();
    }

    onContentClick(event) {
      const image = event.target.closest('img[data-article-image-lightbox]');
      if (!image) return;

      event.preventDefault();
      this.open(image);
    }

    onContentKeydown(event) {
      const image = event.target.closest('img[data-article-image-lightbox]');
      if (!image || !['Enter', ' '].includes(event.key)) return;

      event.preventDefault();
      this.open(image);
    }

    onDialogClick(event) {
      if (event.target === this.dialog || event.target.closest('.article-image-lightbox__close')) {
        this.close();
      }
    }

    onDialogCancel(event) {
      event.preventDefault();
      this.close();
    }

    open(image) {
      this.lightboxImage.src = image.currentSrc || image.src;
      this.lightboxImage.alt = image.alt || '';
      if (this.dialog.open) return;

      if (this.dialog.showModal) {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
    }

    close() {
      if (this.dialog.open && this.dialog.close) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
    }
  }

  customElements.define('article-image-lightbox', ArticleImageLightbox);
}
